import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../../../utils';

// ============================================================================
// Switch - Toggle switch component
// ============================================================================

export interface SwitchProps extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  /** Label for the switch */
  label?: string;
  /** Description text below the label */
  description?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: {
    root: 'h-4 w-7',
    thumb: 'h-3 w-3 data-[state=checked]:translate-x-3',
  },
  md: {
    root: 'h-5 w-9',
    thumb: 'h-4 w-4 data-[state=checked]:translate-x-4',
  },
  lg: {
    root: 'h-6 w-11',
    thumb: 'h-5 w-5 data-[state=checked]:translate-x-5',
  },
};

export const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, label, description, size = 'md', id, ...props }, ref) => {
  const switchId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const sizes = sizeClasses[size];

  const switchElement = (
    <SwitchPrimitive.Root
      ref={ref}
      id={switchId}
      className={cn(
        // agn-switch / agn-switch-thumb are inert hooks: next-components.css
        // (next scope only) repaints track and thumb with semantic tokens.
        'agn-switch peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
        'transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-[var(--action-primary)] data-[state=unchecked]:bg-[var(--input)]',
        sizes.root,
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'agn-switch-thumb pointer-events-none block rounded-full shadow-lg ring-0 transition-transform',
          'bg-[var(--action-primary-fg)]',
          'data-[state=unchecked]:translate-x-0',
          sizes.thumb
        )}
      />
    </SwitchPrimitive.Root>
  );

  if (!label && !description) {
    return switchElement;
  }

  return (
    <div className="flex items-start gap-3">
      {switchElement}
      <div className="flex flex-col">
        {label && (
          <label
            htmlFor={switchId}
            className="text-sm font-medium text-text-primary cursor-pointer leading-none"
          >
            {label}
          </label>
        )}
        {description && (
          <p className="text-sm text-muted-foreground mt-1">
            {description}
          </p>
        )}
      </div>
    </div>
  );
});
Switch.displayName = 'Switch';
